import { useCallback, useEffect } from 'react';

import { API_URL } from '@config';
import { SESSION_STORAGE_KEYS } from '@constants/storage';
import generateSHA256Hash from '@utils/crypto';
import { getSessionStorageItem, setSessionStorageItem } from '@utils/storage';

// Module-level cache (shared across all hook instances)
const resolvedNameCache = new Map<string, string>();
const inflightNameRequests = new Map<string, Promise<string | null>>();

/**
 * Parses file path to extract fileId and accessKey
 * File path format: "fileId|accessKey"
 */
const parseFileInfo = (fileName: string) => {
  const parts = fileName.split('|');
  return {
    fileId: parts[0] || '',
    accessKey: parts[1] || ''
  };
};

/**
 * Loads cache from sessionStorage on module initialization
 */
const loadSessionCache = () => {
  try {
    const parsed = getSessionStorageItem(
      SESSION_STORAGE_KEYS.SUPPORTING_DOC_NAME_CACHE
    ) as Record<string, string> | null;
    if (!parsed) return;
    Object.entries(parsed).forEach(([k, v]) => {
      if (k && v) resolvedNameCache.set(k, v);
    });
  } catch {
    // Ignore errors (e.g., private mode, corrupted data)
  }
};

/**
 * Persists cache to sessionStorage
 */
const persistSessionCache = () => {
  try {
    const obj: Record<string, string> = {};
    resolvedNameCache.forEach((v, k) => {
      obj[k] = v;
    });
    setSessionStorageItem(SESSION_STORAGE_KEYS.SUPPORTING_DOC_NAME_CACHE, obj);
  } catch {
    // Ignore errors (quota exceeded, private mode)
  }
};

/**
 * Fetches file metadata from API and extracts display name
 */
const fetchFileMetadata = async (filePath: string): Promise<string | null> => {
  const { accessKey, fileId } = parseFileInfo(filePath);
  if (!accessKey || !fileId) return null;

  try {
    const signatureInput = `${fileId}|${accessKey}`;
    const signature = await generateSHA256Hash(signatureInput);

    const redirectUrl = `${API_URL}/_files/download/${fileId}/${accessKey}?signature=${signature}&isExport=false`;
    const presignedResponse = await fetch(redirectUrl).then((res) =>
      res.json()
    );

    if (!presignedResponse) return null;

    const presignedUrl =
      typeof presignedResponse === 'string'
        ? presignedResponse
        : presignedResponse?.url || presignedResponse?.presignedUrl;
    const fileNameFromPresigned =
      typeof presignedResponse === 'object'
        ? presignedResponse?.fileName ||
          presignedResponse?.filename ||
          presignedResponse?.name
        : undefined;

    if (!presignedUrl) return null;

    // Derive filename from URL or use provided name
    const fileName =
      fileNameFromPresigned ||
      decodeURIComponent(
        presignedUrl.split('/').pop()?.split('?')[0] || fileId
      );

    return fileName;
  } catch {
    return null;
  }
};

/**
 * Custom hook for managing file name caching
 * Provides functionality to resolve display names for remote files
 *
 * @returns Object containing fetchDisplayFileName function
 */
const useFileNameCache = () => {
  // Load cache on first hook usage
  useEffect(() => {
    loadSessionCache();
  }, []);

  /**
   * Fetches display file name for a remote file path
   * Uses caching to avoid duplicate API calls
   *
   * @param filePath - File path in format "fileId|accessKey"
   * @returns Promise resolving to display name or null
   */
  const fetchDisplayFileName = useCallback(
    async (filePath: string): Promise<string | null> => {
      // Check cache first
      const cached = resolvedNameCache.get(filePath);
      if (cached) return cached;

      // Check if request is already in flight
      const inflight = inflightNameRequests.get(filePath);
      if (inflight) return inflight;

      // Create new request
      const request = (async () => {
        try {
          const resolved = await fetchFileMetadata(filePath);
          if (resolved) {
            resolvedNameCache.set(filePath, resolved);
            persistSessionCache();
          }
          return resolved || null;
        } catch {
          return null;
        } finally {
          inflightNameRequests.delete(filePath);
        }
      })();

      inflightNameRequests.set(filePath, request);
      return request;
    },
    []
  );

  /**
   * Checks if a file path is a remote file (contains '|')
   */
  const isRemoteFile = useCallback(
    (filePath: string): boolean => filePath.includes('|'),
    []
  );

  /**
   * Gets cached file name if available
   */
  const getCachedFileName = useCallback(
    (filePath: string): string | null =>
      resolvedNameCache.get(filePath) || null,
    []
  );

  return {
    fetchDisplayFileName,
    isRemoteFile,
    getCachedFileName,
    parseFileInfo
  };
};

export default useFileNameCache;
